import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Shield, Users, Settings, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { toast } from "sonner";
import {
  listProfiles,
  changeProfileStatus,
  type Profile,
} from "@/services/profilesService";
import { ProfileForm } from "./ProfileForm";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/auth/PermissionGuard";

interface ProfileManagementProps {
  onBack: () => void;
}

type ViewMode = "list" | "form";

export const ProfileManagement = ({ onBack }: ProfileManagementProps) => {
  const { canCreate, canUpdate } = usePermissions();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Carregar perfis ao montar componente
  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const profilesResponse = await listProfiles();

      if (profilesResponse.success && profilesResponse.data) {
        setProfiles(profilesResponse.data.data);
      } else {
        toast.error(profilesResponse.error?.message || "Erro ao carregar perfis");
      }
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    await loadProfiles();
    setViewMode("list");
    setEditingProfile(null);
  };

  const handleNewProfile = () => {
    setEditingProfile(null);
    setViewMode("form");
  };

  const handleEdit = (profile: Profile) => {
    setEditingProfile(profile);
    setViewMode("form");
  };

  const toggleProfileStatus = async (profileId: string) => {
    const profile = profiles.find(p => p.id === profileId);
    if (!profile) return;

    try {
      const response = await changeProfileStatus(profileId, !profile.is_active);

      if (response.success) {
        toast.success("Status do perfil alterado!");
        await loadProfiles();
      } else {
        toast.error(response.error?.message || "Erro ao alterar status");
      }
    } catch (error) {
      toast.error("Erro ao alterar status");
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <Badge variant="default" className="bg-green-500/10 text-green-700 border-green-200">
        Ativo
      </Badge>
    ) : (
      <Badge variant="secondary" className="bg-gray-500/10 text-gray-700 border-gray-200">
        Inativo
      </Badge>
    );
  };

  const getPermissionIcon = (permissionId: string) => {
    if (permissionId.includes('manage')) return <Settings className="h-3 w-3" />;
    if (permissionId.includes('workspace')) return <Users className="h-3 w-3" />;
    if (permissionId.includes('view') || permissionId.includes('access')) return <Shield className="h-3 w-3" />;
    return <Users className="h-3 w-3" />;
  };

  if (viewMode === "form") {
    return (
      <ProfileForm
        onBack={() => setViewMode("list")}
        onSave={handleSaveProfile}
        editingProfile={editingProfile}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando perfis...</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard
      permission="res_profiles.read"
      fallback={
        <div className="min-h-screen flex items-center justify-center p-8">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Acesso negado</CardTitle>
              <CardDescription>Você não tem permissão para visualizar perfis.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      }
    >
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Shield}
        title="Gerenciar Perfis"
        description="Configuração de perfis e permissões"
        onBack={onBack}
        actions={
          canCreate("profiles") && (
            <Button
              className="bg-gradient-primary text-white hover:opacity-90"
              onClick={handleNewProfile}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Perfil
            </Button>
          )
        }
      />

      {/* Main Content */}
      <main className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span>Perfis de Acesso</span>
            </CardTitle>
            <CardDescription>
              Gerencie os perfis de acesso e suas permissões no sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Permissões</TableHead>
                    <TableHead>Usuários</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {profiles.map((profile) => (
                    <TableRow key={profile.id}>
                      <TableCell className="font-medium">{profile.name}</TableCell>
                      <TableCell className="max-w-xs">
                        <div className="truncate" title={profile.description}>
                          {profile.description}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {profile.permissions.slice(0, 3).map((permission) => (
                            <Badge key={permission.id} variant="outline" className="text-xs">
                              {getPermissionIcon(permission.name)}
                              <span className="ml-1">{permission.display_name.split(' ')[0]}</span>
                            </Badge>
                          ))}
                          {profile.permissions.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{profile.permissions.length - 3}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {profile.users_count} usuários
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(profile.is_active)}</TableCell>
                      <TableCell>{new Date(profile.created_at).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          {canUpdate("profiles") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(profile)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}

                          {canUpdate("profiles") && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleProfileStatus(profile.id)}
                              className={
                                profile.is_active
                                  ? "hover:bg-destructive/10 hover:text-destructive"
                                  : "hover:bg-green-50 hover:text-green-700"
                              }
                            >
                              {profile.is_active ? "Desativar" : "Ativar"}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
    </PermissionGuard>
  );
};
